// ═══════════════════════════════════════════════════════════════════════════
//  ΚΑΝΟΝΑΣ ΗΜΕΡΟΜΗΝΙΩΝ v2 — ΕΥΡΩΠΑΪΚΗ ΜΟΡΦΗ ΠΑΝΤΟΥ
//  ---------------------------------------------------------------------------
//  ΕΜΦΑΝΙΣΗ : ημέρα/μήνας/έτος → 20/04/2027       (ΠΟΤΕ 04/20/2027)
//  ΒΑΣΗ     : ISO → 2027-04-20                    (αμετάβλητο)
//
//  ⚠️ ΠΟΤΕ `new Date('20/04/2027')`. Ο V8 το διαβάζει ως ΜΗΝΑΣ/ΗΜΕΡΑ και
//     είτε βγάζει λάθος ημερομηνία είτε Invalid Date. Κάθε parse γίνεται με
//     ΡΗΤΟ split εδώ. Ούτε `toLocaleDateString()` χωρίς locale — παίρνει το
//     locale του browser, που σε αγγλικά Windows είναι US.
// ═══════════════════════════════════════════════════════════════════════════

export const EU_LOCALE = 'el-GR';

/** '2027-04-20' | '2027-04-20T10:00:00Z' | Date → '20/04/2027'. Κενό → ''. */
export function formatDateEU(v?: string | Date | null): string {
  if (v === null || v === undefined || v === '') return '';
  if (v instanceof Date) {
    if (isNaN(v.getTime())) return '';
    return `${pad(v.getDate())}/${pad(v.getMonth() + 1)}/${v.getFullYear()}`;
  }
  const s = String(v).trim();
  // ήδη ευρωπαϊκό DD/MM/YYYY → πέρασέ το ως έχει
  const eu = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (eu) return `${pad(+eu[1])}/${pad(+eu[2])}/${eu[3]}`;
  // ISO YYYY-MM-DD (με ή χωρίς ώρα)
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return s;   // άγνωστη μορφή → ανέγγιχτη, ΠΟΤΕ μάντεμα
}

/* ═══════════════════════════════════════════════════════════════════════════
   ΩΡΑ ΕΛΛΑΔΑΣ ΣΤΗΝ ΕΜΦΑΝΙΣΗ — UTC ΣΤΗ ΒΑΣΗ
   ---------------------------------------------------------------------------
   Ο server, η PostgreSQL και η PHP τρέχουν σε UTC. Οι στήλες είναι
   `timestamp WITHOUT time zone`, οπότε το `created_at` φτάνει ως
   «2026-09-02 12:14:45» — ΧΩΡΙΣ ζώνη, αλλά είναι UTC.

   ΤΟ ΛΑΘΟΣ: η οθόνη τύπωνε τα ψηφία ως είχαν. Μια πληρωμή στις 15:06 Αθήνας
   εμφανιζόταν «12:06» — τρεις ώρες πίσω, σε πίνακα που ο υπάλληλος διαβάζει
   για να πει «πότε μπήκαν τα λεφτά».

   Ο ΚΑΝΟΝΑΣ: ό,τι έχει ΩΡΑ μετατρέπεται σε Europe/Athens. Ό,τι είναι ΣΚΕΤΗ
   ΗΜΕΡΟΜΗΝΙΑ (ημ. έναρξης ναύλου, λήξη δόσης, γέννηση) μένει ανέγγιχτο — δεν
   έχει ώρα να μετατραπεί, και μια μετατροπή θα το μετακινούσε μια μέρα.
   ΣΤΑΘΕΡΗ ΖΩΝΗ, ΟΧΙ ΣΤΑΘΕΡΟ ΟΦΣΕΤ: +3 το καλοκαίρι, +2 τον χειμώνα — το
   αναλαμβάνει το Intl, δεν το υπολογίζουμε εμείς.
   ═══════════════════════════════════════════════════════════════════════════ */
export const EU_TZ = 'Europe/Athens';

/** Το χρονικό σημείο μιας τιμής, ή null όταν δεν έχει ώρα / δεν διαβάζεται. */
function instantOf(v: string | Date): Date | null {
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const s = String(v).trim();
  // Ρητή ζώνη (…Z, …+03:00, …+00) → ο parser του browser ξέρει τι κάνει.
  if (/[T ]\d{2}:\d{2}/.test(s) && /(Z|[+-]\d{2}(:?\d{2})?)$/.test(s)) {
    /* Η PostgreSQL γράφει «+00», ο parser θέλει «+00:00» — αλλιώς Invalid Date
       και η ώρα θα έπεφτε σιωπηλά πίσω στην ανέγγιχτη μορφή. */
    const iso = s.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00');
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }
  // Χωρίς ζώνη αλλά ΜΕ ώρα → είναι UTC (έτσι το γράφει ο server).
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m) return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] || 0)));
  return null;   // σκέτη ημερομηνία ή άγνωστη μορφή
}

/** Τα μέρη μιας στιγμής όπως τα βλέπει η Αθήνα. */
function athensParts(d: Date): Record<string, string> {
  const f = new Intl.DateTimeFormat('en-GB', {
    timeZone: EU_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const out: Record<string, string> = {};
  f.formatToParts(d).forEach(p => { out[p.type] = p.value; });
  return out;
}

/** → '20/04/2027 14:30' σε ώρα Ελλάδας. Χωρίς ώρα → μόνο η ημερομηνία. */
export function formatDateTimeEU(v?: string | Date | null): string {
  if (v === null || v === undefined || v === '') return '';
  const d = instantOf(v as any);
  if (!d) return formatDateEU(v);          // σκέτη ημερομηνία — ανέγγιχτη
  const p = athensParts(d);
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}

/** Μόνο η ημερομηνία μιας στιγμής, σε ώρα Ελλάδας (π.χ. πληρωμή 23:40 UTC
    → 03/09, όχι 02/09). Για σκέτες ημερομηνίες ταυτίζεται με το formatDateEU. */
export function formatDateLocalEU(v?: string | Date | null): string {
  if (v === null || v === undefined || v === '') return '';
  const d = instantOf(v as any);
  if (!d) return formatDateEU(v);
  const p = athensParts(d);
  return `${p.day}/${p.month}/${p.year}`;
}

/** Μόνο η ώρα, σε ώρα Ελλάδας. Κενό όταν η τιμή δεν έχει ώρα. */
export function formatTimeEU(v?: string | Date | null): string {
  if (v === null || v === undefined || v === '') return '';
  const d = instantOf(v as any);
  if (!d) return '';
  const p = athensParts(d);
  return `${p.hour}:${p.minute}`;
}

/** '20/04/2027' ή '2027-04-20' → Date. Άκυρο → null. ΠΟΤΕ new Date(string). */
export function parseEUDate(v?: string | Date | null): Date | null {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const s = String(v).trim();
  const eu = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (eu) return mk(+eu[3], +eu[2], +eu[1]);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return mk(+iso[1], +iso[2], +iso[3]);
  return null;
}

/** Το έτος μιας ημερομηνίας οποιασδήποτε μορφής. Άκυρο → null. */
export function yearOf(v?: string | Date | null): number | null {
  const d = parseEUDate(v);
  return d ? d.getFullYear() : null;
}

/** Date | 'DD/MM/YYYY' → 'YYYY-MM-DD' για αποθήκευση/API. */
export function toISODate(v?: string | Date | null): string {
  const d = parseEUDate(v);
  if (!d) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function pad(n: number): string { return String(n).padStart(2, '0'); }
function mk(y: number, m: number, d: number): Date {
  const dt = new Date(y, m - 1, d);   // τοπική ζώνη, χωρίς ολίσθηση UTC
  return isNaN(dt.getTime()) ? null as any : dt;
}
